import { useMemo } from "react";
import clsx from "clsx";
import { sample } from "lodash";

import VocabularyType from "@/types/vocabulary-type";
import { stageAssetPreloader } from "@/utils/stage-asset-preloader";
import { useMediaCache } from "@/hooks/use-media-cache";

type VocabularyProps = {
  vocabulary: VocabularyType;
  className?: string;
};

export const Vocabulary = ({ vocabulary, className }: VocabularyProps) => {
  // Try to use the preloader's selected image first, fallback to random selection
  const imageUrl = useMemo(() => {
    const preloadedImageUrl = stageAssetPreloader.getSelectedVocabularyImage(vocabulary.id);
    
    if (preloadedImageUrl) {
      return preloadedImageUrl;
    }
    
    // Fallback: random selection if preloader hasn't selected yet
    // This should rarely happen as preloader runs before rendering
    return sample(vocabulary.images)?.url;
  }, [vocabulary.id]); // Remove vocabulary.images from deps to avoid re-selecting on song data refresh

  const cachedUrl = useMediaCache(imageUrl || null, 'image');

  return (
    <img
      src={cachedUrl || imageUrl} // Use cached URL if available, fallback to original
      alt={vocabulary.name}
      loading="eager"
      fetchPriority="high"
      className={clsx(
        "w-full h-full max-h-[25svh] md:max-h-[500px] aspect-square object-contain rounded-lg ring-4 ring-gray-200",
        className,
      )}
    />
  );
};
